/**
 * Perpendicular / T-junction wall obstructions for single-room opening edits.
 *
 * When an opening is resized or repositioned along its host wall, the candidate
 * span must not slide behind a partition wall that T-juncts into the host —
 * including a partition that meets the *far* side of a shared wall, which the
 * user can't see from the single-room view. This module computes the forbidden
 * along-wall intervals those T-junction walls occupy.
 *
 * See claude-docs/2026-06-12-single-room-measurement-editing.md §1.4.
 *
 * Reuses the canonical point-onto-segment projection (geometryUtils) and the
 * wall axis helper (wallGeometryUtils) rather than reinventing either. The
 * interior-band constants mirror shared-lib/geometry/wall-graph.js so the
 * client and the pipeline agree on what counts as a junction.
 */
import { projectPointOntoSegment } from './geometryUtils';
import { getWallAxisXZ } from './wallGeometryUtils';

const EPS = 1e-6;
// An endpoint must land strictly inside the host span to count as a T-junction
// (not at a shared corner). Mirrors wall-graph.js's 0.02 / 0.98 band.
const T_BAND_LOW = 0.02;
const T_BAND_HIGH = 0.98;

// Wall thickness lives on `thickness` for structure walls; `dimensions.z` is
// the rectangular fallback. Absent → 0 (degenerate zero-width interval that
// never blocks — graceful, not a guessed default).
const wallThickness = (wall) => wall?.thickness ?? wall?.dimensions?.z ?? 0;

// Endpoints of a wall in the XZ plane, derived from its axis (alignment-first,
// rotation/center fallback inside getWallAxisXZ).
const wallEndpointsXZ = (wall) => {
  const axis = getWallAxisXZ(wall);
  if (!axis || !axis.len) return null;
  const hx = (axis.dirX * axis.len) / 2;
  const hz = (axis.dirZ * axis.len) / 2;
  return {
    start: [axis.midX - hx, axis.midZ - hz],
    end: [axis.midX + hx, axis.midZ + hz],
    axis,
  };
};

/**
 * Forbidden along-wall intervals where another structure wall T-juncts the
 * host wall's interior span.
 *
 * @param {Object} hostWall - structure wall the opening lives on (alignment + thickness + id)
 * @param {Array} structureWalls - all structure walls (structureData.walls)
 * @returns {Array<{ startM: number, endM: number, wallId: string|null }>}
 *          intervals in meters from the host wall's start.
 */
export const computePerpWallIntervals = (hostWall, structureWalls) => {
  const host = wallEndpointsXZ(hostWall);
  if (!host) return [];
  const hostSeg = { start: host.start, end: host.end };
  const hostLen = host.axis.len;
  const hostHalf = wallThickness(hostWall) / 2;

  const intervals = [];
  for (const w of structureWalls || []) {
    if (!w || w.id === hostWall.id) continue;
    const other = wallEndpointsXZ(w);
    if (!other) continue;
    const wHalf = wallThickness(w) / 2;
    // The endpoint must actually reach the host body, not merely be near the
    // host's centerline — allow anywhere from the centerline out to where the
    // two wall bodies just touch.
    const touchTol = hostHalf + wHalf + EPS;

    for (const ep of [other.start, other.end]) {
      const proj = projectPointOntoSegment(ep, hostSeg);
      if (!proj) continue;
      // Corner / outside the span (t clamps to 0 or 1 past the ends) → skip.
      if (proj.t <= T_BAND_LOW || proj.t >= T_BAND_HIGH) continue;
      if (proj.distance > touchTol) continue;
      const junctionM = proj.t * hostLen;
      intervals.push({
        startM: junctionM - wHalf,
        endM: junctionM + wHalf,
        wallId: w.id ?? null,
      });
      break; // one interval per T-juncting wall is enough
    }
  }
  return intervals;
};

/**
 * True iff the candidate span [s, e] (meters from host start) overlaps none of
 * the forbidden intervals. Same half-open overlap test used for sibling
 * openings: clear iff `e <= startM || s >= endM` for every interval.
 *
 * @param {[number, number]} span
 * @param {Array<{ startM: number, endM: number }>} intervals
 * @returns {boolean}
 */
export const isSpanClear = (span, intervals) => {
  const [s, e] = span;
  for (const iv of intervals || []) {
    if (!(e <= iv.startM || s >= iv.endM)) return false;
  }
  return true;
};

/**
 * The first interval the span collides with (for surfacing which wall blocks).
 * Returns null when clear.
 */
export const firstBlockingInterval = (span, intervals) => {
  const [s, e] = span;
  for (const iv of intervals || []) {
    if (!(e <= iv.startM || s >= iv.endM)) return iv;
  }
  return null;
};
